// src/utils/notify.js
//
// ONE way to tell the user something happened. Replaces antd `message`, antd `notification`,
// react-toastify and `alert()`, which all looked and behaved differently.
//
//   notify.success("Booking saved")
//   notify.error("Could not save", "The room is no longer available")      // title + description
//   notify.error({ title: "Could not save", description: "...", duration: 10 })
//   notify.apiError(err, "Could not save the booking")                       // pulls the real reason out of the API error
//   const hide = notify.loading("Generating PDF...");  ...  hide();
//   if (await confirmAction({ title: "Delete this room?", danger: true, okText: "Delete" })) { ... }
//
// Look and feel (position, duration, stacking, theme) is set in one place: App.js (<AntdApp .../>) and hms-ui.css (.hms-toast).
import React from "react";
import { App, message as staticMessage, notification as staticNotification, Modal as staticModal } from "antd";

// The themed instances are registered by <NotifyBridge/> once the app has mounted.
// Before that (or in tests) the static antd ones are used, so a call never fails.
let notificationApi = staticNotification;
let messageApi = staticMessage;
let modalApi = staticModal;

export const NotifyBridge = () => {
  const { notification, message, modal } = App.useApp();
  React.useEffect(() => {
    notificationApi = notification;
    messageApi = message;
    modalApi = modal;
    return () => {
      notificationApi = staticNotification;
      messageApi = staticMessage;
      modalApi = staticModal;
    };
  }, [notification, message, modal]);
  return null;
};

/* ───────── turn any API / JS error into one sentence a person can act on ───────── */
export const getErrorMessage = (err, fallback = "Something went wrong. Please try again.") => {
  if (!err) return fallback;
  if (typeof err === "string") return err;

  const res = err.response;
  if (!res) {
    if (err.code === "ERR_NETWORK" || err.message === "Network Error") return "Cannot reach the server. Check your connection and try again.";
    if (err.code === "ECONNABORTED") return "The request took too long. Please try again.";
    return err.message || fallback;
  }

  const d = res.data;
  const fromBody = typeof d === "string" ? d : d?.message ?? d?.Message ?? d?.title ?? d?.Title ?? d?.error ?? d?.detail;
  if (typeof fromBody === "string" && fromBody.trim() && fromBody.length < 300 && !/<html|<!doctype/i.test(fromBody)) return fromBody.trim();

  // ASP.NET validation: { errors: { Field: ["message"] } }
  if (d && typeof d.errors === "object") {
    const first = Object.values(d.errors).flat()[0];
    if (first) return String(first);
  }

  switch (res.status) {
    case 401:
      return "Your session has expired. Please sign in again.";
    case 403:
      return "You do not have permission to do that.";
    case 404:
      return "We could not find what you asked for.";
    case 409:
      return "This was changed by someone else. Reload and try again.";
    case 429:
      return "Too many requests. Please wait a moment and try again.";
    default:
      return res.status >= 500 ? "The server had a problem. Please try again in a moment." : fallback;
  }
};

/* ───────── toasts ───────── */
const DURATION = { success: 3.5, info: 4, warning: 5, error: 7 }; // seconds
const LABEL = { success: "Done", info: "Note", warning: "Check this", error: "Something went wrong" };
const LONG_TITLE = 90;

// Accepts what the old call sites passed: a string, a node, antd's { message|title, description, duration, key },
// react-toastify's (text, { autoClose, toastId }) and antd's (text, duration).
const normalize = (type, content, opts) => {
  let title;
  let description;
  let duration;
  let key;

  if (content && typeof content === "object" && !React.isValidElement(content)) {
    title = content.title ?? content.message ?? content.content;
    description = content.description;
    duration = content.duration;
    key = content.key ?? content.toastId;
    if (duration === undefined && content.autoClose !== undefined) duration = content.autoClose === false ? 0 : content.autoClose / 1000;
  } else {
    title = content;
  }

  if (typeof opts === "string" || React.isValidElement(opts)) {
    description = description ?? opts;
  } else if (typeof opts === "number") {
    duration = duration ?? opts;
  } else if (opts && typeof opts === "object") {
    description = description ?? opts.description;
    duration = duration ?? opts.duration;
    key = key ?? opts.key ?? opts.toastId;
    if (duration === undefined && opts.autoClose !== undefined) duration = opts.autoClose === false ? 0 : opts.autoClose / 1000;
  }

  // A long sentence reads better as the body, under a short heading
  if (description === undefined && typeof title === "string" && title.length > LONG_TITLE) {
    description = title;
    title = LABEL[type];
  }

  if (title === undefined || title === null || title === "") title = LABEL[type];
  // the same message shown twice replaces itself instead of stacking
  if (key === undefined && typeof title === "string" && (description === undefined || typeof description === "string")) {
    key = `${type}|${title}|${description ?? ""}`;
  }
  return { title, description, duration: duration ?? DURATION[type], key };
};

const show = (rawType, content, opts) => {
  const type = rawType === "warn" ? "warning" : rawType;
  const { title, description, duration, key } = normalize(type, content, opts);
  notificationApi[type]({
    title,
    description,
    duration,
    key,
    className: `hms-toast hms-toast-${type}`,
  });
  return key;
};

const notify = (content, opts) => show("info", content, opts);
notify.success = (c, o) => show("success", c, o);
notify.error = (c, o) => show("error", c, o);
notify.warning = (c, o) => show("warning", c, o);
notify.warn = notify.warning;
notify.info = (c, o) => show("info", c, o);
/** Show the real reason an API call failed. */
notify.apiError = (err, fallback) => show("error", getErrorMessage(err, fallback));
/** Small spinner pill for work in progress. Returns a function that hides it. */
notify.loading = (content, duration = 0) => messageApi.loading(content, duration);
notify.close = (key) => notificationApi.destroy(key);
notify.destroy = () => {
  notificationApi.destroy();
  messageApi.destroy();
};

/* ───────── confirmation dialog ───────── */
/** Promise-based replacement for window.confirm(): `if (!(await confirmAction({...}))) return;` */
export const confirmAction = ({ title = "Are you sure?", content, okText = "Confirm", cancelText = "Cancel", danger = false, width = 420 } = {}) =>
  new Promise((resolve) => {
    let settled = false;
    const done = (v) => {
      if (!settled) {
        settled = true;
        resolve(v);
      }
    };
    modalApi.confirm({
      title,
      content,
      okText,
      cancelText,
      width,
      centered: true,
      icon: null,
      maskClosable: true,
      className: "hms-confirm",
      okButtonProps: { danger },
      onOk: () => done(true),
      onCancel: () => done(false),
      afterClose: () => done(false),
    });
  });

export { notify };
export default notify;
